
import { ReactNode, useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { getAuthToken, getUserType } from '@/utils/authUtils';
import { RefreshCw } from 'lucide-react';

interface ProtectedCandidateRouteProps {
  children: ReactNode;
  requiredPreviousStep?: string;
  stepName: string;
}

const ProtectedCandidateRoute = ({ 
  children, 
  requiredPreviousStep, 
  stepName 
}: ProtectedCandidateRouteProps) => {
  const [isAuthorized, setIsAuthorized] = useState<boolean | null>(null);
  const location = useLocation();

  useEffect(() => {
    const checkAuthorization = () => {
      // Check if user is logged in
      const authToken = getAuthToken();
      const userType = getUserType();

      // If not logged in as a candidate, redirect to login
      if (!authToken || userType !== 'candidate') {
        toast.error('Please sign in as a candidate to continue');
        setIsAuthorized(false);
        return;
      }

      // If this route requires a previous step to be completed
      if (requiredPreviousStep) {
        const previousStepData = sessionStorage.getItem(requiredPreviousStep);
        
        if (!previousStepData) {
          toast.error(`You must complete the previous step before accessing ${stepName}`);
          setIsAuthorized(false);
          return;
        }
      }

      setIsAuthorized(true);
    };

    checkAuthorization();
  }, [requiredPreviousStep, stepName]);

  if (isAuthorized === null) {
    // Still checking authorization
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <RefreshCw className="animate-spin h-12 w-12 text-apple-blue" />
          <p className="text-apple-text-secondary">Verifying authorization...</p>
        </div>
      </div>
    );
  }

  return isAuthorized 
    ? <>{children}</> 
    : <Navigate to="/login" state={{ from: location }} replace />;
};

export default ProtectedCandidateRoute;
